import React, { KeyboardEvent, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCurrentUserFullName,
  selectCurrentUser,
} from '../../store/slices/auth/authSelectors';
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import ArrowUpLineIcon from '@rsuite/icons/ArrowUpLine';
import MenuIcon from '@rsuite/icons/Menu';
import { useMediaQuery } from 'rsuite';
import { logout, resetAuthState } from '../../store/slices/auth/authSlice';
import { useAppDispatch } from '../../store/store';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { resetDashboardState } from '../../store/slices/dashboard/dashboardSlice';
import { resetApplyState } from '../../store/slices/apply/applySlice';
import { resetRenewState } from '../../store/slices/renew/renewSlice';
import { resetUpdateState } from '../../store/slices/update/updateSlice';
import { resetStepState } from '../../store/slices/flow/flowSlice';
import { resetLicenseViewState } from '../../store/slices/licenseView/licenseViewSlice';
import { resetTaxState } from '../../store/slices/tax/taxSlice';
import { terminateSession } from '../../store/slices/auth/authThunks';
import { clearPersistedState } from '../../common/tokenPersistence';
import { resetAdminUpdateState } from '../../store/slices/adminUpdate/adminUpdateSlice';

const AccountDropdown = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedUserFullName = useSelector(selectCurrentUserFullName);
  const currentUser = useSelector(selectCurrentUser);

  const isUserDetailsLoaded =
    currentUser && currentUser.firstName && currentUser.lastName;

  const displayName = isUserDetailsLoaded ? selectedUserFullName : '';

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: globalThis.MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const onLogoutClicked = () => {
    close();
    dispatch(logout());
    dispatch(terminateSession());
    dispatch(resetDashboardState());
    dispatch(resetApplyState());
    dispatch(resetRenewState());
    dispatch(resetUpdateState());
    dispatch(resetStepState());
    dispatch(resetLicenseViewState());
    dispatch(resetTaxState());
    dispatch(resetAuthState());
    dispatch(resetAdminUpdateState());
    clearPersistedState();
    placeNavigate('/');
  };

  const onEditDetailsClicked = () => {
    close();
    placeNavigate('/account-update');
  };

  const [isSmallScreen] = useMediaQuery('(max-width: 768px)');
  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <div
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Account menu"
        data-cy="account-dropdown"
        onClick={toggle}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
          if ((e.key === 'Tab' || e.key === 'Escape') && isOpen) {
            close();
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        {isSmallScreen ? (
          <MenuIcon
            aria-hidden="true"
            style={{ fontSize: '24px' }}
          />
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
            <span className="text-bold">{displayName}</span>
            {isOpen ? (
              <ArrowUpLineIcon
                aria-hidden="true"
                style={{ fontSize: '24px' }}
              />
            ) : (
              <ArrowDownLineIcon
                aria-hidden="true"
                style={{ fontSize: '24px' }}
              />
            )}
          </span>
        )}
      </div>

      {isOpen && (
        <ul
          role="menu"
          style={{
            display: 'block',
            position: 'absolute',
            right: 0,
            top: '100%',
            margin: 0,
            padding: '6px 0',
            listStyle: 'none',
            background: '#fff',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            minWidth: '210px',
            zIndex: 9999,
          }}
        >
          {isSmallScreen && isUserDetailsLoaded && (
            <li
              role="none"
              style={{
                padding: '8px 16px',
                textAlign: 'right',
                color: '#8e8e93',
                fontSize: '14px',
              }}
            >
              {displayName}
            </li>
          )}
          <li
            role="none"
            style={{ margin: 0 }}
          >
            <button
              role="menuitem"
              tabIndex={-1}
              onClick={onEditDetailsClicked}
              className="account-dropdown-menu-item"
            >
              Change my personal details
            </button>
          </li>
          <li
            role="none"
            style={{ margin: 0 }}
          >
            <button
              role="menuitem"
              tabIndex={-1}
              onClick={onLogoutClicked}
              className="account-dropdown-menu-item"
            >
              Logout
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default AccountDropdown;
